import { BaseComponent } from '../../../shared/base-components/base.component';
import { Component, Inject } from '@angular/core';
import { MvdModalService } from '../../../shared/services/modal/mvd-modal.service';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA} from '@angular/material/legacy-dialog';
import { PlatformLocation } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { AssistedSelfServiceType } from '../../models/assisted-self-service-invitation/assisted-self-service-type.model';
import { AddAssistedSelfServiceInvitation } from '../../models/assisted-self-service-invitation/add-assisted-self-service-invitation.model';
import { AssistedSelfServiceType as AssistedSelfServiceTypeEnum } from '../../enums/assisted-self-service-invitation/assisted-self-service-type.enum';
import { ControlValidationMessagesService } from '../../../shared/validators/control-validation-messages.service';
import { AssistedSelfServiceInvitationService } from '../../services/assisted-self-service-invitation/assisted-self-service-invitation.service';

export class AssistedSelfServiceInvitationContext {

    constructor(public customerNumberId: number, public documentId: number = null, public phoneNumber: string = null, public email: string = null, public serviceTypeId: number = null) { }
}

@Component({
    templateUrl: './assisted-self-service-invitation-modal.html'
})

export class AssistedSelfServiceInvitationModalComponent extends BaseComponent {
    public form: UntypedFormGroup;
    public loaded = false;
    public isSubmitted = false;
    public showButtonLoading = false;
    public serviceTypes: AssistedSelfServiceType[];
    private customerNumberId: number;
    private customerAccountNumberId: number;
    private documentId: number;
    private knowledgeTestTypeId = new AssistedSelfServiceTypeEnum().KnowledgeTest.id.toString();

    constructor(public location: PlatformLocation, public fb: UntypedFormBuilder,
        public service: AssistedSelfServiceInvitationService,
        public dialogRef: MatDialogRef<AssistedSelfServiceInvitationModalComponent>,
        public mvdModalService: MvdModalService,
        @Inject(MAT_DIALOG_DATA) public data: AssistedSelfServiceInvitationContext) {
        super();

        this.customerNumberId = this.data.customerNumberId;
        this.documentId = this.data.documentId;
        location.onPopState(() => {
            this.dialogRef.close();
        });
    }

    ngOnInit() {
        this.form = this.fb.group({
            'assistedSelfServiceTypeId': [null, Validators.required],
            'daysExpiresIn': ['1', Validators.required],
            'notificationType': ['1'],
            'message': [null],
            'email': [this.data.email, Validators.compose([Validators.required, ControlValidationMessagesService.optionalEmailValidator])],
            'phoneNumber': [{ value: this.data.phoneNumber, disabled: true }, Validators.required]
        });

        var observables = [this.service.getCustomerPrimaryAccount(this.customerNumberId), this.service.getCustomerAssistedSelfServiceTypes()];
        forkJoin(observables).pipe(
            finalize(() => {
                this.loaded = true;
            }))
            .subscribe(
                (data) => {
                    this.customerAccountNumberId = data[0].id;
                    let val = new AssistedSelfServiceTypeEnum().GetAll().find(d => d.id == this.data.serviceTypeId);
                    if (val) {
                        this.form.controls['assistedSelfServiceTypeId'].patchValue(val.id);
                        this.form.controls['assistedSelfServiceTypeId'].disable();
                        this.form.markAsDirty();
                    } else if (this.documentId) {
                        this.form.controls['assistedSelfServiceTypeId'].patchValue(new AssistedSelfServiceTypeEnum().ESign.id);
                        this.form.controls['assistedSelfServiceTypeId'].disable();
                    } else {
                        const assistedSelfServiceTypes = new AssistedSelfServiceTypeEnum();
                        const disabledAssistedSelfServiceTypeIds = [
                            assistedSelfServiceTypes.ESign.id,
                            assistedSelfServiceTypes.AZMVDNowClaimInvitation.id
                        ];
                        data[1] = data[1].filter(s => !disabledAssistedSelfServiceTypeIds.includes(s.id));
                    }

                    this.serviceTypes = data[1];
                },
                (error) => {
                    this.handleValidationResult(error);
                });

        this.form.controls['notificationType'].valueChanges.subscribe(_ => {
            this.handleNotificationtype();
        });

        this.form.controls['assistedSelfServiceTypeId'].valueChanges.subscribe((assistedSelfServiceTypeId) => {
            if (assistedSelfServiceTypeId === this.knowledgeTestTypeId) {
                this.form.controls['email'].setValidators([ControlValidationMessagesService.optionalEmailValidator]);
                this.form.controls['phoneNumber'].clearValidators();

                if (!this.form.controls['phoneNumber'].value && !this.form.controls['email'].value) {
                    this.form.controls['notificationType'].setValue(null, { emitEvent: false });
                    this.form.controls['email'].disable();
                    this.form.controls['phoneNumber'].disable();
                }
            } else {
                this.form.controls['email'].setValidators([Validators.required, ControlValidationMessagesService.optionalEmailValidator]);
                this.form.controls['phoneNumber'].setValidators([Validators.required]);

                this.handleNotificationtype();
            }

            this.form.updateValueAndValidity();
        });
    }

    get isContactMethodRequired(): boolean {
        return this.form.controls['assistedSelfServiceTypeId'].value !== this.knowledgeTestTypeId;
    }

    private handleNotificationtype(): void {
        if (!this.form.controls['notificationType'].value) {
            this.form.controls['notificationType'].setValue('1', { emitEvent: false });
        }

        if (this.form.controls['notificationType'].value == '1') {
            this.form.controls['email'].enable();
            this.form.controls['phoneNumber'].disable();
        }

        if (this.form.controls['notificationType'].value == '2') {
            this.form.controls['phoneNumber'].enable();
            this.form.controls['email'].disable();
        }
    }

    public cancel() {
        this.dialogRef.close();
    }

    public saveInvitation() {
        this.isSubmitted = true;
        if (this.form.valid) {
            this.showButtonLoading = true;
            let invitation: AddAssistedSelfServiceInvitation = Object.assign(new AddAssistedSelfServiceInvitation(), this.form.value);
            invitation.assistedSelfServiceTypeId = this.form.controls['assistedSelfServiceTypeId'].value;
            invitation.customerNumberId = this.customerNumberId;
            invitation.customerAccountNumberId = this.customerAccountNumberId;
            invitation.documentId = this.documentId;
            this.service.addAssistedSelfServiceInvitation(invitation).pipe(
                finalize(() => {
                    this.showButtonLoading = false;
                }))
                .subscribe(
                    (data) => {
                        this.dialogRef.close(data);
                    },
                    (error) => {
                        this.handleValidationResult(error);
                    });
        }
    }
}
